import React, { useEffect, useRef, useState, useCallback } from "react";

export default function ImageAdjuster({ file, aspectRatio = 1, onConfirm, onCancel }) {
  const canvasRef = useRef();
  const imgRef    = useRef(new Image());
  const dragging  = useRef(false);
  const lastPos   = useRef({ x: 0, y: 0 });

  const [zoom,   setZoom]   = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);

  const CW = 300;
  const CH = Math.round(CW / aspectRatio);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    imgRef.current.onload = () => { setLoaded(true); setZoom(1); setOffset({ x: 0, y: 0 }); };
    imgRef.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!loaded) return;
    const ctx   = canvasRef.current.getContext("2d");
    const img   = imgRef.current;
    const scale = Math.max(CW / img.width, CH / img.height) * zoom;
    const w = img.width * scale, h = img.height * scale;
    const cx = Math.max(Math.min(0, CW - w), Math.min(0, offset.x));
    const cy = Math.max(Math.min(0, CH - h), Math.min(0, offset.y));
    ctx.clearRect(0, 0, CW, CH);
    ctx.drawImage(img, cx, cy, w, h);
  }, [loaded, zoom, offset, CW, CH]);

  const onMouseDown = (e) => { dragging.current = true; lastPos.current = { x: e.clientX, y: e.clientY }; };
  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPos.current.x, dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setOffset(p => ({ x: p.x + dx, y: p.y + dy }));
  }, []);
  const onMouseUp = () => { dragging.current = false; };

  const onTouchStart = (e) => { dragging.current = true; lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchMove  = (e) => {
    if (!dragging.current) return;
    const dx = e.touches[0].clientX - lastPos.current.x, dy = e.touches[0].clientY - lastPos.current.y;
    lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    setOffset(p => ({ x: p.x + dx, y: p.y + dy }));
  };

  const handleConfirm = () => canvasRef.current.toBlob(blob => onConfirm(new File([blob], file.name, { type: "image/jpeg" })), "image/jpeg", 0.92);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", zIndex: 110000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#111827", borderRadius: 16, padding: 24, width: "min(92vw, 340px)", boxShadow: "0 24px 60px rgba(0,0,0,0.7)", border: "1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Adjust Image</span>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 400 }}>drag to reposition</span>
        </div>
        <div style={{ borderRadius: aspectRatio === 1 ? "50%" : 10, overflow: "hidden", width: CW, height: CH, border: "2px solid rgba(245,112,17,0.6)", cursor: "grab", userSelect: "none" }}>
          <canvas ref={canvasRef} width={CW} height={CH}
            onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onMouseUp}
            style={{ display: "block", cursor: "grab" }} />
        </div>
        <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>🔍</span>
          <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={e => setZoom(parseFloat(e.target.value))} style={{ flex: 1, accentColor: "#f57011" }} />
          <span style={{ fontSize: 12, color: "#64748b", minWidth: 36 }}>{Math.round(zoom * 100)}%</span>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid #334155", background: "transparent", color: "#94a3b8", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleConfirm} style={{ flex: 2, padding: "10px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#f57011,#ea6a0a)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>✓ Apply</button>
        </div>
      </div>
    </div>
  );
}
